/**
 * PermissionChip — um item de permissão da matriz, com a máquina de 4 estados:
 *
 *   - "checked"   — liberada diretamente (em `selecionados`)
 *   - "inherited" — vem do cargo (em `herdados`, sem negação ativa)
 *   - "revoked"   — vem do cargo MAS foi negada só para esta pessoa
 *   - "off"       — não liberada
 *
 * Desenho: linha de lista (caixa de marcar + nome + selo + "?"), não mural de
 * pílulas — o nome da permissão é uma frase ("Liberar lote para o corte") e
 * precisa de espaço para ser lido. O "?" fica FORA do <label>: clicar nele
 * abre a ajuda sem marcar/desmarcar a permissão.
 */
import React from "react";
import { FiLock } from "react-icons/fi";

import AjudaPermissao from "./AjudaPermissao.jsx";
import { rotuloPermissao } from "./arvore.js";
import { ItemCaixa, ItemLabel, ItemLinha, ItemSelo, ItemTexto } from "./styles.jsx";

export { rotuloPermissao as rotuloAcao };

const MARCA_CAIXA = { checked: "✓", inherited: "✓", revoked: "✕", off: "" };

export function estadoDaPermissao(codigo, { selecionados, herdados, revogados }) {
  const isHerdada = herdados.has(codigo);
  if (isHerdada && revogados.has(codigo)) return "revoked";
  if (selecionados.has(codigo)) return "checked";
  if (isHerdada) return "inherited";
  return "off";
}

function PermissionChip({
  permissao: p,
  selecionados,
  herdados,
  revogados,
  onToggle,
  herdadasInterativas,
  disabled,
  rotulo,
  onde,
  indice,
}) {
  const state = estadoDaPermissao(p.codigo, { selecionados, herdados, revogados });
  const isHerdada = herdados.has(p.codigo);
  const isRevogada = state === "revoked";

  // Herdada só fica bloqueada quando não é interativa (allowRevoke=false e
  // inheritedReadOnly=true). Revogada é sempre interativa (pra poder voltar).
  const herdadaBloqueada = isHerdada && !isRevogada && !herdadasInterativas;
  const isDisabled = disabled || herdadaBloqueada;

  // Próximo estado ao clicar:
  //   off       -> granted (true para retrocompat)
  //   checked   -> default (false para retrocompat)
  //   inherited -> revoked (só se herdadasInterativas)
  //   revoked   -> default (volta a herdada ativa)
  const handleClick = () => {
    if (isDisabled) return;
    if (state === "off") onToggle?.(p.codigo, true);
    else if (state === "checked") onToggle?.(p.codigo, false);
    else if (state === "inherited") onToggle?.(p.codigo, "revoked");
    else if (state === "revoked") onToggle?.(p.codigo, "default");
  };

  const texto = rotulo || rotuloPermissao(p);
  const dicaEstado =
    state === "revoked"
      ? "Vem do cargo, mas foi negada para esta pessoa (clique para devolver)"
      : state === "inherited"
      ? herdadaBloqueada
        ? "Vem do cargo"
        : "Vem do cargo (clique para negar só para esta pessoa)"
      : "";

  return (
    <ItemLinha $state={state}>
      <ItemLabel $disabled={isDisabled} title={dicaEstado || undefined}>
        <input
          type="checkbox"
          checked={state === "checked" || state === "inherited"}
          disabled={isDisabled}
          onChange={handleClick}
          aria-label={`${texto}${
            state === "revoked" ? " (negada)" : isHerdada ? " (vem do cargo)" : ""
          }`}
        />
        <ItemCaixa $state={state} aria-hidden="true">
          {MARCA_CAIXA[state]}
        </ItemCaixa>
        <ItemTexto $state={state}>{texto}</ItemTexto>
        {state === "inherited" && <ItemSelo $tom="info">do cargo</ItemSelo>}
        {state === "revoked" && <ItemSelo $tom="danger">negada</ItemSelo>}
        {p.requer_mfa && (
          <ItemSelo $tom="warning" title="Pede o código do autenticador ao usar">
            <FiLock size={10} aria-hidden="true" /> MFA
          </ItemSelo>
        )}
      </ItemLabel>
      <AjudaPermissao permissao={p} estado={state} onde={onde} indice={indice} />
    </ItemLinha>
  );
}

export default PermissionChip;
